import React, { useMemo } from "react";
import { withRouter } from "react-router";
import { usePatientWalletDetail } from "./hooks/usePatientWalletDetail";
import { uiNotification } from "../../../../../services/UINotificationService";
import { tCommon } from "../../../../../i18n/useAppTranslation";
import { ReportStats } from "../../../../../boxes/ReportStats/ReportStats";
import { Box } from "../../../../../shared/Box/Box";
import { Skeleton } from "../../../../../shared/Skeleton/Skeleton";
import { Empty } from "../../../../../shared/Empty/Empty";
import Header from "../../../components/Header";
import { HTTP_ENDPOINTS } from "../../../../../consts/api";
import classes from "./PatientWalletDetail.module.scss";

const PatientWalletDetail = ({ patientId, selectedClinic, history }) => {
  // Fetch data - Patient Wallet Detail API only accepts patient_id and exportType
  const { data, isLoading, error, refetch } = usePatientWalletDetail(
    patientId,
    {}, // No additional params needed for detail API
    {
      staleTime: 0,
      cacheTime: 0,
      onError: () => {
        uiNotification.error(tCommon("error.getReports"));
      },
    },
  );

  // Prepare download params - only patient_id is needed
  const downloadParams = useMemo(
    () => ({
      patient_id: patientId,
    }),
    [patientId],
  );

  // Get clinics from API response for header
  const apiClinics = useMemo(() => {
    return data?.clinics || [];
  }, [data]);

  // Handle back navigation
  const handleBack = () => {
    history.push("/refactor-business-insight/sales/patient_wallet_liability");
  };

  // Prepare breakdown stats using the breakdown object from API
  const breakdownStats = useMemo(() => {
    if (!data?.breakdown) return [];

    return [
      {
        id: "dollar_credits",
        name: "Dollar Credits",
        data: data.breakdown.dollar_credits?.balance || "$0.00",
      },
      {
        id: "prepaid_services",
        name: "Prepaid Services",
        data: data.breakdown.prepaid_services?.balance || "$0.00",
      },
      {
        id: "non_cash_credits",
        name: "Non-Cash Credits",
        data: data.breakdown.non_cash_credits?.balance || "$0.00",
      },
    ];
  }, [data?.breakdown]);

  // Prepare clinics for header
  const clinics = useMemo(() => {
    const clinicOptions = [{ label: "All Clinics", value: 0 }];
    if (apiClinics?.length) {
      clinicOptions.push(
        ...apiClinics.map((clinic) => ({
          label: clinic.clinic_name,
          value: clinic.id,
        })),
      );
    }
    return clinicOptions;
  }, [apiClinics]);

  return (
    <div className="flex-col gap-16">
      <Header
        title={`Patient Wallet Detail${
          data?.patientInfo?.client ? ` - ${data.patientInfo.client}` : ""
        }`}
        clinics={clinics}
        selectedClinic={selectedClinic}
        downloadRoute={HTTP_ENDPOINTS.getPatientWalletDetail()}
        requestParams={downloadParams}
        withBackBtn={true}
        backFunc={handleBack}
      />

      {isLoading && (
        <Box>
          <Skeleton count={5} height={40} borderRadius="0px" />
        </Box>
      )}

      {error && (
        <Box>
          <Empty position="center">
            {`Error Loading Patient Data: Failed to load patient wallet detail: ${
              error.message || "Unknown error"
            }`}
          </Empty>
          <div className={classes.errorActions}>
            <button onClick={() => refetch()} className={classes.retryButton}>
              Retry
            </button>
            <button onClick={handleBack} className={classes.backButton}>
              Back to Summary
            </button>
          </div>
        </Box>
      )}

      {!isLoading && !error && data?.patientInfo && (
        <>
          {/* Patient Information Card */}
          <Box>
            <div className="patient-info-section">
              <h3 className={classes.sectionTitle}>Patient Information</h3>
              <div className={classes.patientDetailsGrid}>
                <div className={classes.detailItem}>
                  <span className={classes.label}>Name:</span>
                  <span className={classes.value}>
                    {data.patientInfo.client || "-"}
                  </span>
                </div>
                <div className={classes.detailItem}>
                  <span className={classes.label}>Email:</span>
                  <span className={classes.value}>
                    {data.patientInfo.email || "-"}
                  </span>
                </div>
                <div className={classes.detailItem}>
                  <span className={classes.label}>Total Outstanding:</span>
                  <span className={`${classes.value} ${classes.outstanding}`}>
                    {data.patientInfo.total_outstanding || "$0.00"}
                  </span>
                </div>
                <div className={classes.detailItem}>
                  <span className={classes.label}>Last Activity:</span>
                  <span className={classes.value}>
                    {data.patientInfo.last_activity_date || "-"}
                  </span>
                </div>
              </div>
            </div>
          </Box>

          {/* Wallet Breakdown */}
          <Box>
            <h3 className={classes.sectionTitle}>Wallet Breakdown</h3>
            <ReportStats stats={breakdownStats} isLoading={isLoading} />
          </Box>

          {/* Additional Information */}
          <Box>
            <h3 className={classes.sectionTitle}>Breakdown Details</h3>
            <div className={classes.breakdownGrid}>
              <div className={classes.breakdownCard}>
                <h4 className={classes.cardTitle}>Dollar Credits</h4>
                <p className={classes.cardDescription}>
                  Direct dollar credits in patient wallet
                </p>
                <p className={`${classes.cardValue} ${classes.dollarCredits}`}>
                  {data.breakdown.dollar_credits.balance}
                </p>
              </div>

              <div className={classes.breakdownCard}>
                <h4 className={classes.cardTitle}>Prepaid Services</h4>
                <p className={classes.cardDescription}>
                  Value of unused prepaid service packages/treatments
                </p>
                <p
                  className={`${classes.cardValue} ${classes.prepaidServices}`}
                >
                  {data.breakdown.prepaid_services.balance}
                </p>
              </div>

              <div className={classes.breakdownCard}>
                <h4 className={classes.cardTitle}>Non-Cash Credits</h4>
                <p className={classes.cardDescription}>
                  Birthday, Aspire, Xperience credits + Aspire certificates
                </p>
                <p className={`${classes.cardValue} ${classes.nonCashCredits}`}>
                  {data.breakdown.non_cash_credits.balance}
                </p>
              </div>
            </div>
          </Box>
        </>
      )}

      {!isLoading && !error && !data?.patientInfo && (
        <Box>
          <Empty position="center">
            No patient data found: Unable to load patient wallet information
          </Empty>
        </Box>
      )}
    </div>
  );
};

export default withRouter(PatientWalletDetail);
